import { Button, Input, Popover, PopoverContent, PopoverTrigger, showConfirmationPrompt } from '@bpinternal/ui-kit-next'
import { useSuspenseQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { useState } from 'react'
import { Copy, List } from '~/elements'
import { useCreateBotApikey } from '~/hooks/bots/useCreateBotApiKey'
import { useDeleteBotApikey } from '~/hooks/bots/useDeleteBotApiKey'
import { listBotApiKeysQueryOptions } from '~/queries/botApiKeys'
import { friendlyFormatISODate } from '~/utils'
import { PlusCircle, Trash2 } from 'lucide-react'

type BotApiKeysProps = {
  botId: string
  workspaceId: string
}

export const BotApiKeys = ({ botId, workspaceId }: BotApiKeysProps) => {
  const { data: apikeys } = useSuspenseQuery(listBotApiKeysQueryOptions({ botId, workspaceId }))
  const { mutate: deleteBotApikey } = useDeleteBotApikey({
    workspaceId,
    botId,
  })
  const { mutate: createBotApikey } = useCreateBotApikey({
    workspaceId,
    botId,
  })
  // const { mutate: setBotUrl, isError, isPending } = useSetBotUrl({ workspaceId, botId })
  const [note, setNote] = useState('')
  const [open, setOpen] = useState(false)
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold">Bot API Keys</h2>
          <p className="text-xs text-neutral-500">Manage your bot's API keys</p>
        </div>
        <div>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild className="items-between flex flex-row">
              <Button className="cursor-pointer">
                <PlusCircle />
                Create API key
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <p className="mb-1 text-sm text-neutral-800">Note</p>
              <Input value={note} onChange={(e) => setNote(e.target.value)} className="mb-2" />
              <div className="flex justify-end">
                <Button
                  className="cursor-pointer"
                  onClick={() => {
                    createBotApikey({ note })
                    setOpen(false)
                    setNote('')
                  }}
                >
                  Create
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <List items={apikeys}>
        {(apikey) => (
          <BakRow
            {...apikey}
            deleteBotApikey={() =>
              showConfirmationPrompt(`Are you sure you want to delete the API Key "${apikey.note}"?`, {
                title: 'Delete API Key',
                variant: 'danger',
              }).then(() => {
                deleteBotApikey({ id: apikey.id })
              })
            }
          />
        )}
      </List>
    </div>
  )
}

type BakRowProps = {
  note: string
  createdAt: string
  value?: string
  id: string
  deleteBotApikey: (props: { id: string }) => void
}

const BakRow = ({ note, createdAt, value, deleteBotApikey, id }: BakRowProps): JSX.Element => (
  <div
    className={cx('flex flex-wrap items-center justify-between p-5', {
      'bg-green-200': !!value,
    })}
  >
    <div className="mr-3">
      <div>
        <b>{note || 'Bot Api Key'}</b>
      </div>
      <div className="text-sm capitalize text-muted-foreground">Created {friendlyFormatISODate(createdAt)}</div>
    </div>
    {value && (
      <div className="mr-3">
        <Copy className="max-w-[500px]" value={value} description="bot api key" />
      </div>
    )}
    <Button
      className="h-4 w-4 p-5 hover:bg-red-100 hover:text-red-600"
      variant="ghost"
      onClick={() => deleteBotApikey({ id })}
    >
      <Trash2 />
    </Button>
  </div>
)
